/**
 * A held slot the patient went on to book outside «ثبت نهایی نوبت» (the regular booking form: missing
 * details, «فرد دیگر», a clinic that asks more, an error), or booked while the service could not be told.
 * Until the service hears `booked`, the seat stays `offered`, expires and goes to the end of the line — so
 * the slot is remembered here and «نوبت‌های من» tells the service once it sees the booking in the patient's
 * own list (or at once, when the book id is already known). `POST /v1/offers/:id/booked` is idempotent and
 * accepts a held or an already expired offer, so trying again is always safe.
 */
export const PENDING_BOOKING_KEY = 'dabbeh-pending-booking';

/** Forgotten after this long: the booking was never made (or was made far too late to matter). */
export const PENDING_BOOKING_TTL_MS = 3 * 24 * 3600_000;

export interface PendingBooking {
  offerId: number;
  slug: string;
  centerId: string;
  /** ISO — the slot's own time (`visit_at`); without it the booking cannot be recognised in the list. */
  visitAt?: string;
  requestCode?: string;
  /** Known when the queue booked the slot itself and only telling the service failed. */
  bookId?: string;
  /** ISO */
  savedAt: string;
}

const isRecord = (value: unknown): value is PendingBooking =>
  !!value &&
  typeof value === 'object' &&
  Number.isFinite((value as PendingBooking).offerId) &&
  typeof (value as PendingBooking).slug === 'string' &&
  typeof (value as PendingBooking).centerId === 'string' &&
  typeof (value as PendingBooking).savedAt === 'string';

const write = (records: PendingBooking[]) => {
  try {
    if (records.length) window.localStorage.setItem(PENDING_BOOKING_KEY, JSON.stringify(records));
    else window.localStorage.removeItem(PENDING_BOOKING_KEY);
  } catch {
    // storage blocked: the seat's own offer is still matched while it is offered
  }
};

/** The slots still waiting for `booked` (expired ones dropped). */
export const readPendingBookings = (now = Date.now()): PendingBooking[] => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(window.localStorage.getItem(PENDING_BOOKING_KEY) ?? '[]');
  } catch {
    return [];
  }
  const list = (Array.isArray(parsed) ? parsed : [parsed]).filter(isRecord);
  const fresh = list.filter(record => {
    const at = Date.parse(record.savedAt);
    return Number.isFinite(at) && now - at < PENDING_BOOKING_TTL_MS;
  });
  if (fresh.length !== list.length) write(fresh);
  return fresh;
};

/** Remembers (or updates) the slot of one offer; a book id already known is kept. */
export const savePendingBooking = (record: PendingBooking) => {
  const list = readPendingBookings();
  const known = list.find(item => item.offerId === record.offerId);
  const rest = list.filter(item => item.offerId !== record.offerId);
  write([...rest, { ...record, bookId: record.bookId ?? known?.bookId }].slice(-5));
};

/** The service heard `booked` (or the record is moot): forget it. Without an id, forgets every one. */
export const clearPendingBooking = (offerId?: number) => {
  if (offerId === undefined) return write([]);
  write(readPendingBookings().filter(item => item.offerId !== offerId));
};

/** One item of «نوبت‌های من»'s own list (the fields this needs; see src/pages/patient/appointments.tsx). */
export interface BookedAppointment {
  book_id?: string | number | null;
  center_id?: string | number | null;
  center?: { id?: string | number | null } | null;
  /** unix seconds of the visit */
  from?: number | string | null;
  delete?: number | null;
}

/**
 * The patient's booking of this held slot in their own list: the same center at the same time (to the
 * minute). The slot is held for this patient alone, so nobody else can hold that booking. Null without a
 * visit time — nothing to recognise it by.
 */
export const findBookedAppointment = (target: { centerId?: string; visitAt?: string }, books: readonly BookedAppointment[] = []) => {
  if (!target.centerId || !target.visitAt) return null;
  const at = Date.parse(target.visitAt);
  if (!Number.isFinite(at)) return null;
  const found = books.find(book => {
    if (!book || book.delete === 1 || book.book_id === null || book.book_id === undefined || book.book_id === '') return false;
    if (String(book.center?.id ?? book.center_id ?? '') !== String(target.centerId)) return false;
    const from = Number(book.from);
    if (!Number.isFinite(from) || from <= 0) return false;
    return Math.abs((from > 1e12 ? from : from * 1000) - at) < 60_000;
  });
  return found ? String(found.book_id) : null;
};
